import {
  getCountByStatus,
  useGetAllCollectors,
} from "@/api/collector/collector";
import NewCollectorDialog from "@/components/collector/NewCollectorDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  collectorsAdvancedSearchConfig,
  collectorsColumns,
} from "@/config/dashboard/collectors";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Collector } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "collector-table-preferences";

const flatVisibleCollectorsColumns = collectorsColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const CollectorDashboard = () => {
  const { t } = useTranslation();

  const {
    countByStatus,
    currentCount: totalCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
  } = useDashboardParams<Collector>({
    resourceName: "collector",
    resourceCounter: getCountByStatus,
    statusField: "collectionneursPublicationStatus",
  });

  const navigate = useNavigate();

  const { data } = useGetAllCollectors(fetchParams);

  const columns: ColumnDef<Collector, any>[] = useMemo(() => {
    return generateTable(collectorsColumns, "artwork");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleCollectorsColumns;
    }
    return flatVisibleCollectorsColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...collectorsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...collectorsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleCollectors")}
        </p>
        <GlobalSearch
          config={collectorsAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          onValueChange={setFilterStatus}
          countByStatus={countByStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={collectorsAdvancedSearchConfig}
            resource="collectors"
          />
          <AdvancedSort config={collectorsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createCcollector")}
            Dialog={NewCollectorDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/collector/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: totalCount,
          }}
          storageKey={STORAGE_KEY}
          columns={columns}
          data={data ?? []}
          options={{
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            enableColumnResizing: true,
          }}
        />
      </div>
    </div>
  );
};

export default CollectorDashboard;
